<script setup lang="ts">
import { type FolderManagerItemProps } from './folder-manager';
import DropdownMenu from '../DropdownMenu/DropdownMenu.vue';
import HugeIcon from '../HugeIcon/HugeIcon.vue';
import { computed, ref } from 'vue';

const props = defineProps<FolderManagerItemProps>();

const iconFolderClosed = props.folder.iconClosed ?? 'folder';
const iconFolderOpen = props.folder.iconOpen ?? 'folder_open';

const emits = defineEmits<{
  select: [];
  drop: [];
}>();

const isHovered = ref(false);
const isDragOver = ref(false);
const hasActionsButton = computed(() => {
  return props.folder.actions && props.folder.actions.length > 0;
});
const showActionsButton = computed(() => {
  return (
    hasActionsButton.value &&
    (props.isTouchDevice || isHovered.value || props.selected)
  );
});

const handleSelect = (e: MouseEvent): void => {
  // if we click the button that opens the dropdown menu, we shouldn't
  // select the item
  const target = e.target as HTMLElement | null;
  if (target?.closest('[data-stop-selection]')) return;
  emits('select');
};

const handleDragOver = (event: DragEvent): void => {
  if (!props.folder.droppable) {
    return;
  }

  event.preventDefault();
  isDragOver.value = true;
};

const handleDrop = (event: DragEvent): void => {
  if (!props.folder.droppable) {
    return;
  }

  event.preventDefault();
  isDragOver.value = false;
  emits('drop');
};
</script>

<template>
  <div
    class="mb-1 flex cursor-pointer items-center gap-2 rounded-md p-2 text-sm hover:bg-white"
    :class="[
      isDragOver && props.folder.droppable
        ? 'text-primary-600 bg-primary-50 ring-primary-300 shadow-sm ring-2'
        : props.selected
          ? 'text-primary-500 bg-white py-2 shadow-sm'
          : 'text-slate-700',
    ]"
    @click="handleSelect"
    @dragenter="handleDragOver"
    @dragover="handleDragOver"
    @dragleave="isDragOver = false"
    @drop="handleDrop"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <div
      class="rounded-md p-1"
      :class="[
        isDragOver && props.folder.droppable
          ? 'text-primary-600 bg-white'
          : props.selected
            ? 'bg-primary-50 text-primary-500'
            : 'text-slate-500',
      ]"
    >
      <HugeIcon
        :icon="props.selected ? iconFolderOpen : iconFolderClosed"
        :size="20"
      />
    </div>
    <div class="min-w-0 flex-1">
      <span
        class="block overflow-hidden font-medium text-ellipsis whitespace-nowrap"
      >
        {{ props.folder.label }}
      </span>
      <span
        v-if="props.folder.subtitle"
        class="mt-0.5 block overflow-hidden text-xs text-ellipsis whitespace-nowrap"
        :class="[props.selected ? 'text-primary-400' : 'text-slate-400']"
      >
        {{ props.folder.subtitle }}
      </span>
    </div>
    <div class="flex items-center self-center">
      <span
        class="rounded-full px-2 py-1 text-xs font-bold"
        :class="[
          isDragOver && props.folder.droppable
            ? 'text-primary-600 bg-white'
            : props.selected
              ? 'bg-primary-50 text-primary-500'
              : 'text-slate-400',
        ]"
        >{{ props.folder.count }}</span
      >
      <DropdownMenu
        v-if="hasActionsButton"
        :items="props.folder.actions!"
        :placement="props.actionsPlacement"
      >
        <button
          v-if="hasActionsButton"
          data-stop-selection="true"
          class="flex items-center justify-center rounded-md bg-slate-100 text-slate-800 transition-all ease-in-out"
          :class="[showActionsButton ? 'ml-2 h-6 w-7' : 'ml-0 h-0 w-0']"
        >
          <HugeIcon icon="ellipsis" :size="16" class="mx-1" />
        </button>
      </DropdownMenu>
    </div>
  </div>
</template>
